import React from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
  Toolbar,
  ToolbarButton,
  Tooltip,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  ErrorCircle24Regular,
  Warning24Regular,
  DocumentBulletList24Regular,
  ArrowLeft20Regular,
  Open20Regular,
} from "@fluentui/react-icons";
import { useDevice } from "../../model/DeviceContext";
import { useAppStyles } from "../styles/appStyles";
import { CarboWasteEndPoints_WebPageUrl } from "../../api/endpoints";

interface SubscriptionPageProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({ onBack, onNavigate }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const { subscription, isRegistered, isTrial, userInfo, refreshSubscription, unregisterDevice } = useDevice();

  const handleClose = () => {
    onBack();
  };

  if (!subscription) {
    return (
      <div className={styles.appPageContainer}>
        <div className={styles.appPageHeader}>
          <DocumentBulletList24Regular className={styles.appPageHeaderIcon} />
          <Text className={styles.appPageTitle}>
            {t("subscription.title", "Subskrypcja")}
          </Text>
        </div>
        <div className={styles.subscriptionContent}>
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{t("subscription.no_data", "Brak danych subskrypcji")}</MessageBarTitle>
              {t("subscription.no_data_desc", "Nie można pobrać informacji o subskrypcji.")}
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  const getStatusBadge = () => {
    if (subscription.status === "Subscribed" && isRegistered) {
      return (
        <div className={`${styles.subscriptionStatusBadge} ${styles.subscriptionStatusActive}`}>
          <CheckmarkCircle24Regular />
          {t("subscription.status.active", "Aktywna")}
        </div>
      );
    }

    if (subscription.status === "Trial" || isTrial) {
      return (
        <div className={`${styles.subscriptionStatusBadge} ${styles.subscriptionStatusTrial}`}>
          <Warning24Regular />
          {t("subscription.status.freeTrial", "Wersja próbna")}
        </div>
      );
    }

    if (subscription.status === "Suspended" || subscription.status === "Cancelled") {
      return (
        <div className={`${styles.subscriptionStatusBadge} ${styles.subscriptionStatusInactive}`}>
          <ErrorCircle24Regular />
          {subscription.status === "Suspended"
            ? t("subscription.status.suspended", "Zawieszona")
            : t("subscription.status.cancelled", "Anulowana")}
        </div>
      );
    }

    return (
      <div className={`${styles.subscriptionStatusBadge} ${styles.subscriptionStatusInactive}`}>
        <ErrorCircle24Regular />
        {subscription.status}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (dateString.includes("9999")) {
        return t("subscription.unlimited", "Bez limitu");
      }
      return date.toLocaleDateString("pl-PL");
    } catch {
      return dateString;
    }
  };

  const totalEups = subscription.companies.reduce((acc, company) => acc + company.eups.length, 0);

  const handleActivate = () => {
    console.log('🔑 Navigate to activation page');
    if (onNavigate) {
      onNavigate('activation');
    }
  };

  const handleBuySubscription = () => {
    console.log('🛒 Kup subskrypcję clicked');
    window.open(CarboWasteEndPoints_WebPageUrl, '_blank');
  };

  const handleUnregister = () => {
    console.log('🗑️ Navigate to unregister page');
    if (onNavigate) {
      onNavigate('unregister');
    }
  };

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <DocumentBulletList24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("subscription.title", "Subskrypcja")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip content={t("subscription.toolbar.back_tooltip", "Powrót do poprzedniej strony")} relationship="description">
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("subscription.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={handleClose}
          >
            {t("subscription.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.subscriptionContent}>
        {/* Status Message */}
        {(subscription.status === "Suspended" || subscription.status === "Cancelled") && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {subscription.status === "Suspended"
                  ? t("subscription.status.suspended", "Zawieszona")
                  : t("subscription.status.cancelled", "Anulowana")}
              </MessageBarTitle>
              {isTrial
                ? t("subscription.trial_mode_enabled", "Dostęp w trybie testowym")
                : t("subscription.limited_access", "Ograniczony dostęp do funkcjonalności")}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Status Section */}
        <div style={{ marginBottom: "24px" }}>
          <div className={styles.subscriptionSectionHeader}>
            <Text className={styles.subscriptionSectionTitle}>
              {t("subscription.details", "Szczegóły subskrypcji")}
            </Text>
          </div>

          {/* User Information */}
          {userInfo && (userInfo.email || userInfo.name) && (
            <>
              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.user", "Użytkownik")}</Text>
                <Text className={styles.subscriptionValue}>
                  {userInfo.name || userInfo.email || t("subscription.user_unknown", "Nieznany")}
                </Text>
              </div>
              {userInfo.email && userInfo.name && (
                <div className={styles.subscriptionInfoRow}>
                  <Text className={styles.subscriptionLabel}>{t("subscription.user_email", "Email")}</Text>
                  <Text className={styles.subscriptionValue}>{userInfo.email}</Text>
                </div>
              )}
            </>
          )}

          {/* Purchaser Information (for SaaS subscriptions) */}
          {subscription.source === "SaaS" && subscription.purchaserEmail && (
            <div className={styles.subscriptionInfoRow}>
              <Text className={styles.subscriptionLabel}>{t("subscription.purchaser", "Nabywca subskrypcji")}</Text>
              <Text className={styles.subscriptionValue}>{subscription.purchaserEmail}</Text>
            </div>
          )}

          <div className={styles.subscriptionInfoRow}>
            <Text className={styles.subscriptionLabel}>{t("subscription.status.label", "Status")}</Text>
            <div className={styles.subscriptionValue}>{getStatusBadge()}</div>
          </div>
        </div>

        {/* Trial Mode - Show action buttons instead of full details */}
        {isTrial ? (
          <div className={styles.subscriptionActionButtonsContainer}>
            <Button
              appearance="primary"
              className={styles.subscriptionActivateButton}
              onClick={handleActivate}
            >
              {t("subscription.activate_app", "Aktywuj aplikację")}
            </Button>
            <Button
              appearance="transparent"
              as="a"
              href={CarboWasteEndPoints_WebPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.appLinkButtonLarge}
            >
              {t("subscription.buy_subscription", "Kup subskrypcję")}
              <Open20Regular />
            </Button>
          </div>
        ) : (
          <>
            {/* Subscription Details */}
            <div style={{ marginBottom: "24px" }}>
              <div className={styles.subscriptionSectionHeader}>
                <Text className={styles.subscriptionSectionTitle}>
                  {t("subscription.plan", "Plan")}
                </Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.plan", "Plan")}</Text>
                <Text className={styles.subscriptionValue}>{subscription.planId}</Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.term_unit", "Model płatności")}</Text>
                <Text className={styles.subscriptionValue}>{subscription.termUnit}</Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.auto_renew", "Automatyczne odnawianie")}</Text>
                <Text className={styles.subscriptionValue}>
                  {subscription.autoRenew 
                    ? t("subscription.auto_renew.yes", "Tak") 
                    : t("subscription.auto_renew.no", "Nie")}
                </Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.start_date", "Data rozpoczęcia")}</Text>
                <Text className={styles.subscriptionValue}>{formatDate(subscription.startDate)}</Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.end_date", "Data zakończenia")}</Text>
                <Text className={styles.subscriptionValue}>{formatDate(subscription.endDate)}</Text>
              </div>

              {subscription.limits?.companiesLimit && (
                <div className={styles.subscriptionInfoRow}>
                  <Text className={styles.subscriptionLabel}>{t("subscription.companies_limit", "Limit podmiotów BDO")}</Text>
                  <Text className={styles.subscriptionValue}>{subscription.limits.companiesLimit}</Text>
                </div>
              )}

              {subscription.limits?.eupsLimit && (
                <div className={styles.subscriptionInfoRow}>
                  <Text className={styles.subscriptionLabel}>{t("subscription.eups_limit", "Limit MPD dla subskrypcji")}</Text>
                  <Text className={styles.subscriptionValue}>{subscription.limits.eupsLimit}</Text>
                </div>
              )}
            </div>

            {/* Subscription Usage Section */}
            <div style={{ marginBottom: "24px" }}>
              <div className={styles.subscriptionSectionHeader}>
                <Text className={styles.subscriptionSectionTitle}>
                  {t("subscription.usage", "Wykorzystanie subskrypcji")}
                </Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.companies_count", "Liczba podmiotów BDO/Limit subskrypcji")}</Text>
                <Text className={styles.subscriptionValue}>{subscription.companies.length} / {subscription.limits?.companiesLimit}</Text>
              </div>

              <div className={styles.subscriptionInfoRow}>
                <Text className={styles.subscriptionLabel}>{t("subscription.eups_count", "Liczba MPD/Limit subskrypcji")}</Text>
                <Text className={styles.subscriptionValue}>{totalEups} / {subscription.limits?.eupsLimit}</Text>
              </div>
            </div>

            {/* BDO Entities Navigation Button */}
            {subscription.companies.length > 0 && onNavigate && (
              <div className={styles.subscriptionActionButtonsContainer}>
                <Button
                  appearance="secondary"
                  className={styles.subscriptionActionButton}
                  onClick={() => onNavigate('bdo-entities')}
                >
                  {t("subscription.view_companies", "Zobacz listę podmiotów BDO i MPD")}
                </Button>
              </div>
            )}

            {/* Add Activation Key Button */}
            <div className={styles.subscriptionActionButtonsContainer}>
              <Button
                appearance="primary"
                className={styles.subscriptionActivateButton}
                onClick={handleActivate}
              >
                {t("subscription.add_activation_key", "Dodaj nowy klucz")}
              </Button>
            </div>

            {/* Unregister Button - only for registered users */}
            {isRegistered && (
              <div className={styles.subscriptionActionButtonsContainer}>
                <Button
                  appearance="primary"
                  className={styles.subscriptionUnregisterButton}
                  onClick={handleUnregister}
                >
                  {t("subscription.unregister", "Odinstaluj klucz")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
